"use client"
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useLoading } from './LoadingContext';
import styles from './LoadingOverlay.module.css';
import { Box3 } from 'three';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from "three";
import useFirstHomeVist from "@/Component/Hooks/FirstHomeVist.jsx";

const LoadingOverlay = () => {
    useFirstHomeVist();

    const { isVisible, isZoomIn, isVisibleLogo, isCompleted } = useLoading();
    return (
        <>
             <div className={`${styles.loading} ${isVisible ? styles.open : ""} ${isZoomIn ? "" : styles.fade} ${isCompleted ? styles.completed : ""}`}>
                <Canvas camera={{ position: [0, 0, 5], fov: 100, near: 0.01, far: 1000 }} gl={{ localClippingEnabled: true, preserveDrawingBuffer: true }} dpr={[1, 2]} linear={false}>
                    <ambientLight intensity={0.8} />
                    
            <group rotation={[Math.PI * 0, 0, Math.PI * 0]} >
                <directionalLight position={[0, 0, 15]} intensity={5} />
            </group>
                    {isVisibleLogo && <LogoModel />}
                    <Animation targetZ={-14.6} />

                </Canvas>
            </div>
        </>
    );
};

export default LoadingOverlay;

const LogoModel = () => {
    const { progress } = useLoading();
    const node = useGLTF('/models/BEPEX_LOGO_3D.glb')
    const boundingBox = useMemo(() => {
        const box = new Box3().setFromObject(node.nodes.Fill);
        return box;
    }, [node]);
    const minX = boundingBox.min.x;
    const maxX = boundingBox.max.x;
    const { viewport } = useThree();
    const totalWidth = (40* (viewport.aspect / 1.3333333333333333)) + (maxX - minX);
    const clippingPosition = (-20* (viewport.aspect / 1.3333333333333333)) + (progress / 100) * totalWidth;
    const scale = [70 * (viewport.aspect / 1.3333333333333333), 20 * (viewport.aspect / 1.3333333333333333), 70 * (viewport.aspect / 1.3333333333333333)];
    const position = [-15 * (viewport.aspect / 1.3333333333333333), 6.5 * (viewport.aspect / 1.3333333333333333), -15];
    return (
        <group>

            <mesh geometry={node.nodes.Fill.geometry} scale={scale} position={position} rotation={[Math.PI * 0.5, Math.PI * 0, Math.PI * 0]}

            >
                {progress <= 100 && <meshStandardMaterial
                    color="white"
                    clippingPlanes={[new THREE.Plane(new THREE.Vector3(-1, 0, 0), clippingPosition)]} // 음수 주의!
                    clipShadows
                />}
            </mesh>
            <mesh geometry={node.nodes.Outline.geometry} scale={scale} position={position} rotation={[Math.PI * 0.5, Math.PI * 0, Math.PI * 0]} />
        </group>
    );
}

const Animation = ({ targetZ }) => {
    const { isZoomIn,isCompleted } = useLoading();
    return (
        <>
            {!isCompleted && <>
                {isZoomIn && <CameraAnimation targetZ={targetZ} />}
                {!isZoomIn && <TransitionSquares />}
            </>}
        </>
    );
}
const CameraAnimation = ({ targetZ }) => {
    const { camera } = useThree();
    const speed = 0.5;
    const tRef = useRef(0); // 누적되는 t 값
    const [start, setStart] = useState(false);
    const { hide, isZoomIn, progress, setIsCompleted, updateProgress } = useLoading();
    const easeOutCubic = t => t * t * t;
    useFrame((state, delta) => {
        if (progress >= 100) {
            if (isZoomIn) {
                setStart(true);
            }
        }
        if (start && camera.position.z > targetZ) {
            tRef.current += delta * speed;
            tRef.current = Math.min(tRef.current, 1); // clamp
            const easedT = easeOutCubic(tRef.current);
            camera.position.z = THREE.MathUtils.lerp(5, targetZ, easedT);
            if (tRef.current >= 1) {
                document.body.style.overflow = "";
                hide();
                setStart(false);
                setIsCompleted(true);
                setTimeout(() => {
                    updateProgress(0);
                    camera.position.z = 5;
                }, 0)
            }

        }
    });
    return null;
}

const RevealMaterial = {
    uniforms: {
        time: { value: 0 },
        rotationProgress: { value: 0 },
        screenTexture: { value: null },
        resolution: { value: new THREE.Vector2() },
        planeSize: { value: new THREE.Vector2() },
        cols: { value: 0 },
        rows: { value: 0 },
    },
    vertexShader: ` 

    uniform float cols;
uniform float rows;
uniform float rotationProgress;

  uniform float time;
  uniform vec2 resolution;
  uniform vec2 planeSize;
varying vec2 vUv;
    varying float vFacing;
float computeDelay(vec2 uv) {
    // 웨이브 형태 딜레이 (좌 → 우, 위 → 아래 대각선)
    vec2 grid = floor(uv * vec2(cols, rows));
  return ((cols - grid.x - 1.0) + (rows - grid.y - 1.0)) * 0.025;
}

float easeInOutCubic(float t) {
    return t < 0.5
        ? 4.0 * t * t * t
        : 1.0 - pow(-2.0 * t + 2.0, 3.0) / 2.0;
}

vec3 rotateY(vec3 pos, float angle,vec2 pivot) {
     pos.x -= pivot.x;
    pos.z -= pivot.y;
    float cosA = cos(angle);
    float sinA = sin(angle);
    float x = pos.x * cosA - pos.z * sinA;
    float z = pos.x * sinA + pos.z * cosA;
    pos.x = x + pivot.x;
    pos.z = z + pivot.y;
    return pos;
}

void main() {
    vUv = uv;

    vec2 cell = floor(uv * vec2(cols, rows));
      vec2 cellCenter = (cell + 0.5) / vec2(cols, rows);
      vec2 pivot = (cellCenter - 0.5) * planeSize;

      float delay = computeDelay(uv);
      float t = clamp((time - delay) * 6.0, 0.0, 1.0);
       float easedT = easeInOutCubic(t); // ✅ easing 적용
      float angle = mix(0.0, 3.1415, easedT);

      vec3 p = position;
      p.xy -= pivot;

      float s = sin(angle);
      float c = cos(angle);
      mat3 rotY = mat3(
        c, 0.0, -s,
        0.0, 1.0, 0.0,
        s, 0.0, c
      );
      p = rotY * p;
      p.xy += pivot;

      vFacing = sign(cos(angle));
      gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}`
    ,
    fragmentShader: `
    
  varying vec2 vUv;
  varying float vDelay;
  uniform float time;
  uniform sampler2D screenTexture;
  
varying float vFacing;
void main() {

    if (vFacing < 0.0) discard;
    gl_FragColor = texture2D(screenTexture, vUv);
}`
};

const TransitionSquares = () => {
    const { progress, setIsVisibleLogo, hide, setIsCompleted, isCompleted} = useLoading();
    const cellSize = 0.5;
    const { gl, scene, camera, viewport } = useThree();
    const hasCaptured = useRef(false);
    const { cols, rows } = useGridSize(cellSize);
    const renderTarget = useMemo(() => new THREE.WebGLRenderTarget(1920, 1080, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat
    }), []);
    const shader = useMemo(() => new THREE.ShaderMaterial({
        ...RevealMaterial,
        transparent: true,
        uniforms: {
            time: { value: 0 },
            screenTexture: { value: null },
            rotationProgress: { value: 0 },
            planeSize: { value: new THREE.Vector2() },
            resolution: { value: new THREE.Vector2() },
            cols: { value: cols },
            rows: { value: rows },
        }
    }), []);
    useEffect(() => {
        shader.uniforms.planeSize.value.x = viewport.width;
        shader.uniforms.planeSize.value.y = viewport.height;
    }, [cols, rows, viewport]);
    useFrame(() => {
        if (!hasCaptured.current && progress >= 100) {
            gl.setRenderTarget(renderTarget);
            scene.background = new THREE.Color("#000000");
            gl.render(scene, camera);
            gl.setRenderTarget(null);
            shader.uniforms.screenTexture.value = renderTarget.texture;
            shader.uniforms.resolution.value.x = renderTarget.texture.width;
            shader.uniforms.resolution.value.y = renderTarget.texture.height;
            hasCaptured.current = true;
            scene.background = null;
            shader.needsUpdate = true;
            setIsVisibleLogo(false);
            hide();
        }

    });
    useFrame((_, delta) => {
        if (progress < 100 && !hasCaptured.current) {
            shader.uniforms.time.value = 0;
            return;
        }


        shader.uniforms.time.value = Math.min(shader.uniforms.time.value + delta*2, 999);
        const maxDelay = (cols + rows - 2) * 0.025;
        if (shader.uniforms.time.value >= maxDelay && !isCompleted) {
            document.body.style.overflow = "";
            setIsCompleted(true);
            shader.uniforms.time.value = Infinity;
            shader.uniforms.screenTexture.value = null;
            hasCaptured.current = false;
        }

    });
    return (
        <mesh>
            <planeGeometry args={[viewport.width, viewport.height, cols * 2 + 1, rows * 2 + 1]} >
            </planeGeometry>
            <primitive attach="material" object={shader} />
        </mesh>

    );
}


const useGridSize = (cellSize = 0.25) => {
    const { viewport } = useThree();

    return useMemo(() => {
        const cols = Math.ceil(viewport.width * window.devicePixelRatio / cellSize);
        const rows = Math.ceil(viewport.height * window.devicePixelRatio / cellSize);
        return { cols, rows };

    }, [viewport, cellSize]);
};